import { forwardRef, type ChangeEvent, type Ref, type ReactNode, type InputHTMLAttributes } from 'react';
import { useStableId } from '@repletoryreach/sdk-utils/hooks';
import './Radio.css';

export interface RadioProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'color' | 'type'> {
  label?: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

export const Radio = forwardRef(function Radio(
  { label, description, error, size = 'sm', id, ...inputProps }: RadioProps,
  ref: Ref<HTMLInputElement>
): ReactNode {
  const auto = useStableId('rr-radio');
  const inputId = id ?? auto;

  /*
   * Inside a <RadioGroup> the group owns which option is chosen, so a child
   * takes its `checked`, its `name` and its change handler from the context
   * unless the caller set them itself. Without this the component compiled,
   * rendered and did nothing: a group showed no selection and clicking an
   * option changed neither the tick nor the value, which is what a broadcast
   * wizard found on 8 September 2026. An explicit prop still wins, so a lone
   * <Radio> outside a group is unaffected.
   */
  const group = useRadioGroupContext();
  const ownValue = inputProps.value;
  const inGroup = group !== null && ownValue !== undefined;
  const checked = inputProps.checked ?? (inGroup ? group.value === ownValue : undefined);
  const name = inputProps.name ?? (inGroup ? group.name : undefined);
  const onChange = inGroup
    ? (event: ChangeEvent<HTMLInputElement>) => {
        group.setValue(String(ownValue));
        inputProps.onChange?.(event);
      }
    : inputProps.onChange;

  return (
    <label htmlFor={inputId} className={`rr-radio rr-radio--size-${size}`}>
      <input
        ref={ref}
        id={inputId}
        type="radio"
        aria-invalid={!!error || undefined}
        {...inputProps}
        name={name}
        checked={checked}
        onChange={onChange}
      />
      <span className="rr-radio__circle" aria-hidden />
      <span className="rr-radio__body">
        {label ? <span className="rr-radio__label">{label}</span> : null}
        {description ? <span className="rr-radio__description">{description}</span> : null}
        {error ? <span className="rr-radio__error">{error}</span> : null}
      </span>
    </label>
  );
});

export interface RadioGroupProps {
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  name?: string;
  label?: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  children?: ReactNode;
}

import { useUncontrolled } from '@repletoryreach/sdk-utils/hooks';
import { createContext, useContext } from 'react';

interface RadioGroupCtx {
  value: string;
  setValue: (v: string) => void;
  name: string;
}
const RadioGroupContext = createContext<RadioGroupCtx | null>(null);

export function useRadioGroupContext(): RadioGroupCtx | null {
  return useContext(RadioGroupContext);
}

/** Group of `<Radio>` children sharing a single value. */
export function RadioGroup({ value, defaultValue, onChange, name, label, description, error, required, children }: RadioGroupProps): ReactNode {
  const [val, setVal] = useUncontrolled<string>({
    value,
    defaultValue,
    finalValue: '',
    onChange
  });
  const auto = useStableId('rr-radio-group');
  const groupName = name ?? auto;
  return (
    <RadioGroupContext.Provider value={{ value: val, setValue: setVal, name: groupName }}>
      <fieldset className="rr-radio-group">
        {label ? <legend className="rr-radio-group__label">{label}{required ? <span aria-hidden> *</span> : null}</legend> : null}
        {description ? <div className="rr-radio-group__description">{description}</div> : null}
        <div className="rr-radio-group__items" role="radiogroup" aria-label={typeof label === 'string' ? label : undefined}>
          {children}
        </div>
        {error ? <div className="rr-radio-group__error">{error}</div> : null}
      </fieldset>
    </RadioGroupContext.Provider>
  );
}
